import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alignment, Intent } from '@blueprintjs/core';
import moment from 'moment';

import { Breadcrumbs } from 'components/navigation';
import { Button } from 'components/buttons';
import { FormDateRangeInput, FormSelect, FormSwitch, FormTextInput } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';

import { HelperFunctions } from 'helpers';
import { useDebounce } from 'hooks/useDebounce';
import { useRestrictedAccountIds } from 'hooks/useRestrictedAccountIds';
import { RulesEngineService } from 'services';
import { SetRulesEngineDiffViewerReturn, SetRulesEngineDriver, SetRulesEngineRange } from 'state/actions';
import buildXlsxWorkbook from 'utils/buildXlsxWorkbook';

import { isDateRangeValid } from 'pages/internal-admin/rules-engine';
import { DiffViewerDifferences } from './DiffViewerDifferences';
import { DiffViewerGatePanel } from './DiffViewerGatePanel';
import { DiffViewerGroups } from './DiffViewerGroups';
import { buildDiffViewerFileName, buildDiffViewerJson, buildDiffViewerSheets } from './diffViewerDownloads';
import { formatMode, formatValue } from './diffViewerFormatting';
import { buildDiffFilters, buildDiffViewerSearchParams, comparisonModes, labelFilters, parseDiffViewerQuery, positiveIntegerPattern } from './diffViewerQuery';
import { mapCoverageResponse, mapDiffViewerSummary, mapDifferencesResponse, mapGroupsResponse } from './diffViewerResponses';

const requiredAction = "Internal";
const harnessRoute = "/internal-admin/rules-engine/harness";
const isoDateFormat = "YYYY-MM-DD";
const badRequestCode = 400;
const allOption = "all";
const driverDebounceTimeout = 750;

const differencesErrorMessage = "Could not load the differences. Please try again.";
const groupsErrorMessage = "Could not load the difference groups. Please try again.";
const downloadErrorMessage = "Could not build the download. Please try again.";
const utcNotice = "Times marked (UTC) are UTC and are not converted to UK local time.";
const invalidRangeMessage = "The end date cannot be before the start date.";
const invalidDriverMessage = "Enter the driver's employee id as a number.";

const introTexts = {
    [comparisonModes.legacyVersusShadow]: "Every difference the comparison recorded between the legacy engine and RE2, grouped by what they have in common. Open a group to see its differences, and open a difference in the harness to rerun its driver.",
    [comparisonModes.underTest]: "Every difference the comparison recorded between the production engine and the RE2 candidate under test, grouped by what they have in common. Open a group to see its differences, and open a difference in the harness to rerun its driver."
};

const modeItems = [
    { id: comparisonModes.legacyVersusShadow, name: formatMode(comparisonModes.legacyVersusShadow) },
    { id: comparisonModes.underTest, name: formatMode(comparisonModes.underTest) }
];

const labelItems = [
    { id: allOption, name: "All labels" },
    { id: labelFilters.untriaged, name: "Untriaged" },
    { id: labelFilters.knownDifference, name: "Known difference" },
    { id: labelFilters.regression, name: "Regression" }
];

const signOffLabels = [labelFilters.knownDifference, labelFilters.regression];

const defaultArray = [];

// The harness reruns the window the comparison last evaluated. Without one it opens on the UK week the
// difference falls in, with a week either side, and never past today because the harness cannot run ahead.
function buildHarnessRange(difference) {
    const today = moment();
    let start;
    let end;

    if (difference.lastEvaluatedWindowStart != null && difference.lastEvaluatedRunDate != null) {
        start = moment.utc(difference.lastEvaluatedWindowStart);
        end = moment.utc(difference.lastEvaluatedRunDate);
    } else {
        const occurredLocal = moment.utc(difference.occurredAt).local();

        start = occurredLocal.clone().startOf('isoWeek').subtract(1, 'weeks');
        end = occurredLocal.clone().endOf('isoWeek').add(1, 'weeks');
    }

    if (end.isAfter(today)) {
        end = today;
    }

    return {
        startDate: start.format(isoDateFormat),
        endDate: end.format(isoDateFormat)
    };
}

function showReadError(error, message) {
    if (error?.status === badRequestCode) {
        NotificationToaster.show(Intent.WARNING, error.data);
        return;
    }

    NotificationToaster.show(Intent.DANGER, message);
}

export function RulesEngineDiffViewer({ breadcrumbs = defaultArray }) {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const diffViewerReturn = useSelector((state) => state.rulesEngine.diffViewerReturn);

    const query = parseDiffViewerQuery(searchParams);
    const filters = buildDiffFilters(query);
    const filtersKey = JSON.stringify(filters);

    const canAccessPage = UserHasAccess(requiredAction);
    const { blockedReason } = useRestrictedAccountIds({ enabled: canAccessPage });

    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryFailed, setSummaryFailed] = useState(false);
    const [groups, setGroups] = useState(defaultArray);
    const [groupsLoading, setGroupsLoading] = useState(true);
    const [groupsFailed, setGroupsFailed] = useState(false);
    const [coverage, setCoverage] = useState(null);
    const [coverageFailed, setCoverageFailed] = useState(false);
    const [groupDifferences, setGroupDifferences] = useState(defaultArray);
    const [groupLoading, setGroupLoading] = useState(query.group != null);
    const [groupFailed, setGroupFailed] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [periodError, setPeriodError] = useState("");
    const [driverFilterText, setDriverFilterText] = useState(query.employee == null ? "" : `${query.employee}`);

    // Filters can change while reads are in flight, so only the most recent read of each kind may
    // write to the page.
    const latestSummaryRequest = useRef(0);
    const latestCoverageRequest = useRef(0);
    const latestGroupRequest = useRef(0);
    const scrollRestored = useRef(false);

    const debouncedDriverFilter = useDebounce(driverFilterText, driverDebounceTimeout);

    // With no period chosen the reads are sent no period and the picker stays empty.
    const periodFrom = query.from;
    const periodTo = query.to;
    const periodChosen = periodFrom != null && periodTo != null;

    useEffect(() => {
        dispatch({ type: "SITE_FULL_WIDTH" });
    }, []);

    useEffect(loadSummaryAndGroups, [filtersKey, canAccessPage]);
    useEffect(loadCoverage, [query.from, query.to, query.mode, canAccessPage]);
    useEffect(loadGroupDifferences, [filtersKey, query.group, canAccessPage]);
    useEffect(applyDriverFilter, [debouncedDriverFilter]);
    useEffect(restoreScroll, [summaryLoading, groupsLoading, groupLoading]);

    function loadSummaryAndGroups() {
        if (!canAccessPage) {
            return;
        }

        const requestId = latestSummaryRequest.current + 1;
        latestSummaryRequest.current = requestId;

        setSummaryLoading(true);
        setSummaryFailed(false);
        setGroupsLoading(true);
        setGroupsFailed(false);

        const reads = RulesEngineService.getDiffViewerReads(filters);

        reads.summary.then((response) => {
            if (requestId === latestSummaryRequest.current) {
                setSummary(mapDiffViewerSummary(response));
                setSummaryFailed(false);
            }
        }, (error) => {
            if (requestId === latestSummaryRequest.current) {
                setSummary(null);
                setSummaryFailed(true);
                showReadError(error, differencesErrorMessage);
            }
        }).finally(() => {
            if (requestId === latestSummaryRequest.current) {
                setSummaryLoading(false);
            }
        });

        reads.groups.then((response) => {
            if (requestId === latestSummaryRequest.current) {
                setGroups(mapGroupsResponse(response));
                setGroupsFailed(false);
            }
        }, (error) => {
            if (requestId === latestSummaryRequest.current) {
                setGroups(defaultArray);
                setGroupsFailed(true);
                showReadError(error, groupsErrorMessage);
            }
        }).finally(() => {
            if (requestId === latestSummaryRequest.current) {
                setGroupsLoading(false);
            }
        });
    }

    // The open group's rows are read again with the groups, so one retry recovers the whole list.
    function retryReads() {
        loadSummaryAndGroups();
        loadGroupDifferences();
    }

    // Coverage needs both dates, so it is read only once a period is chosen.
    function loadCoverage() {
        const requestId = latestCoverageRequest.current + 1;
        latestCoverageRequest.current = requestId;

        setCoverage(null);
        setCoverageFailed(false);

        if (!canAccessPage || !periodChosen) {
            return;
        }

        // Coverage measures only the legacy versus RE2 comparison.
        if (query.mode === comparisonModes.underTest) {
            return;
        }

        RulesEngineService.getInfringementDiffCoverage(query.from, query.to).then((response) => {
            if (requestId === latestCoverageRequest.current) {
                setCoverage(mapCoverageResponse(response));
                setCoverageFailed(false);
            }
        }, () => {
            if (requestId === latestCoverageRequest.current) {
                setCoverage(null);
                setCoverageFailed(true);
            }
        });
    }

    function loadGroupDifferences() {
        const requestId = latestGroupRequest.current + 1;
        latestGroupRequest.current = requestId;

        setGroupFailed(false);

        if (!canAccessPage || query.group == null) {
            setGroupDifferences(defaultArray);
            setGroupLoading(false);
            return;
        }

        setGroupLoading(true);

        RulesEngineService.getInfringementDiffs({ ...filters, signature: query.group }).then((response) => {
            if (requestId === latestGroupRequest.current) {
                setGroupDifferences(mapDifferencesResponse(response).differences);
                setGroupFailed(false);
            }
        }, (error) => {
            if (requestId === latestGroupRequest.current) {
                setGroupDifferences(defaultArray);
                setGroupFailed(true);
                showReadError(error, differencesErrorMessage);
            }
        }).finally(() => {
            if (requestId === latestGroupRequest.current) {
                setGroupLoading(false);
            }
        });
    }

    // Coming back from the harness to the view it was opened from puts the operator back where they
    // were on the page, once, after the rows it was scrolled over are on screen again.
    function restoreScroll() {
        if (scrollRestored.current || summaryLoading || groupsLoading || groupLoading) {
            return;
        }

        scrollRestored.current = true;

        if (diffViewerReturn == null || diffViewerReturn.search !== location.search) {
            return;
        }

        window.scrollTo(0, diffViewerReturn.scrollY);
    }

    function updateQuery(change) {
        const nextQuery = { ...query, page: 1, ...change };

        setSearchParams(buildDiffViewerSearchParams(nextQuery), { replace: true });
    }

    function applyDriverFilter() {
        const trimmed = debouncedDriverFilter.trim();
        let employee = null;

        if (trimmed !== "") {
            if (!positiveIntegerPattern.test(trimmed)) {
                return;
            }

            employee = Number(trimmed);
        }

        if (employee !== query.employee) {
            updateQuery({ employee: employee });
        }
    }

    function onDriverFilterChange(event) {
        setDriverFilterText(event.target.value);
    }

    function onPeriodChange(range) {
        const from = range.startDate.dateString;
        const to = range.endDate.dateString;

        if (from == null && to == null) {
            setPeriodError("");
            updateQuery({ from: null, to: null });
            return;
        }

        if (from == null || to == null) {
            return;
        }

        if (!isDateRangeValid(from, to)) {
            setPeriodError(invalidRangeMessage);
            return;
        }

        setPeriodError("");
        updateQuery({ from: from, to: to });
    }

    function onModeSelect(item) {
        updateQuery({ mode: item.id });
    }

    function onTypeSelect(item) {
        updateQuery({ type: item.id === allOption ? null : Number(item.id) });
    }

    // A sign-off label only matches signed-off differences, so choosing one shows them too.
    function onLabelSelect(item) {
        if (item.id === allOption) {
            updateQuery({ label: null });
            return;
        }

        if (signOffLabels.includes(item.id)) {
            updateQuery({ label: item.id, signedOff: true });
            return;
        }

        updateQuery({ label: item.id });
    }

    function onProvisionalToggle(event) {
        updateQuery({ provisional: event.target.checked });
    }

    function onWhitelistedToggle(event) {
        updateQuery({ whitelisted: event.target.checked });
    }

    function onSignedOffToggle(event) {
        updateQuery({ signedOff: event.target.checked });
    }

    function onToggleGroup(signature) {
        updateQuery({ group: signature === query.group ? null : signature });
    }

    function onPageChange(newPage) {
        updateQuery({ page: newPage });
    }

    // The harness's way back to the viewer returns to the view being left, not an older one.
    function rememberView() {
        dispatch(SetRulesEngineDiffViewerReturn({ search: location.search, scrollY: window.scrollY }));
    }

    function onBackToHarness() {
        rememberView();
        navigate(harnessRoute);
    }

    function onOpenInHarness(difference) {
        dispatch(SetRulesEngineDriver(difference.driver));
        dispatch(SetRulesEngineRange(buildHarnessRange(difference)));
        rememberView();
        navigate(harnessRoute);
    }

    // Both downloads cover every difference the filters admit, so they read again without the open group.
    function download(save) {
        setDownloading(true);

        RulesEngineService.getInfringementDiffs(filters).then((response) => {
            return save(response);
        }, (error) => {
            showReadError(error, differencesErrorMessage);
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, downloadErrorMessage);
        }).finally(() => {
            setDownloading(false);
        });
    }

    // Named for the period the read reports, else the one on screen; with no period, for the mode alone.
    function buildFileNameModel(differencesModel) {
        const period = [
            { from: differencesModel.from, to: differencesModel.to },
            { from: periodFrom, to: periodTo }
        ].find((candidate) => candidate.from != null && candidate.to != null);

        return {
            mode: differencesModel.mode ?? query.mode,
            from: period?.from ?? null,
            to: period?.to ?? null
        };
    }

    function onDownloadExcel() {
        download((response) => {
            const differencesModel = mapDifferencesResponse(response);

            return buildXlsxWorkbook(buildDiffViewerSheets(differencesModel.differences, groups)).then((workbook) => {
                HelperFunctions.downloadFile(workbook, buildDiffViewerFileName(buildFileNameModel(differencesModel), "xlsx"));
            });
        });
    }

    function onDownloadJson() {
        download((response) => {
            const differencesModel = mapDifferencesResponse(response);
            const json = new Blob([buildDiffViewerJson(response, RulesEngineService.buildInfringementDiffQuery(filters))], { type: "application/json" });

            HelperFunctions.downloadFile(json, buildDiffViewerFileName(buildFileNameModel(differencesModel), "json"));
        });
    }

    // A type is named by its code, since one type spans many sub codes; two types sharing a code are told
    // apart by their ids.
    function buildTypeItems() {
        const typeOptions = summary?.typeOptions ?? defaultArray;
        const sharedCodes = typeOptions
            .map((option) => option.code)
            .filter((code, index, codes) => codes.indexOf(code) !== index);

        function buildTypeName(option) {
            if (sharedCodes.includes(option.code)) {
                return `${formatValue(option.code)} (type ${option.id})`;
            }

            return formatValue(option.code);
        }

        const items = [
            { id: allOption, name: "All infringement types" },
            ...typeOptions.map((option) => ({ id: `${option.id}`, name: buildTypeName(option) }))
        ];

        if (query.type != null && !typeOptions.some((option) => option.id === query.type)) {
            items.push({ id: `${query.type}`, name: `Type ${query.type}` });
        }

        return items;
    }

    const whitelistedCount = summary?.whitelistedCount ?? 0;
    const showWhitelistedHidden = !query.whitelisted && summary != null && !summaryLoading && whitelistedCount > 0;
    let driverFilterError = "";

    if (driverFilterText.trim() !== "" && !positiveIntegerPattern.test(driverFilterText.trim())) {
        driverFilterError = invalidDriverMessage;
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <Fragment>
                    <div className="row">
                        <Breadcrumbs items={breadcrumbs} />
                        <h1>Rules Engine Diff Viewer</h1>

                        <div className="intro-text">
                            <p id="rules-engine-diff-intro">{introTexts[query.mode]}</p>
                        </div>

                        <div className="spacer-bottom-medium inline-items">
                            <Button
                                id="rules-engine-diff-back"
                                text="Back to harness"
                                icon="arrow-left"
                                onClick={onBackToHarness}
                            />
                            <Button
                                id="rules-engine-diff-download"
                                text="Download Excel"
                                icon="download"
                                loading={downloading}
                                disabled={downloading}
                                onClick={onDownloadExcel}
                            />
                            <Button
                                id="rules-engine-diff-download-json"
                                text="Download JSON"
                                icon="code"
                                loading={downloading}
                                disabled={downloading}
                                onClick={onDownloadJson}
                            />
                        </div>

                        <NotificationInline
                            intent="warning"
                            show
                            allowClose={false}
                            text={utcNotice}
                        />

                        <DiffViewerGatePanel
                            gate={summary?.gate ?? null}
                            gateFailed={summaryFailed}
                            coverage={coverage}
                            coverageFailed={coverageFailed}
                            coverageMeasured={query.mode !== comparisonModes.underTest}
                            periodChosen={periodChosen}
                            loading={summaryLoading && summary == null}
                        />

                        <div className="spacer-bottom-small inline-items inline-items-wrap" id="rules-engine-diff-filters">
                            <FormDateRangeInput
                                id="rules-engine-diff-period"
                                headingText="Period"
                                startDate={periodFrom}
                                endDate={periodTo}
                                maxDate={moment().toDate()}
                                dangerHelperText={periodError}
                                onChange={onPeriodChange}
                            />
                            <FormSelect
                                id="rules-engine-diff-mode"
                                headingText="Comparison"
                                items={modeItems}
                                selectedValue={query.mode}
                                onItemSelect={onModeSelect}
                            />
                            <FormSelect
                                id="rules-engine-diff-type"
                                headingText="Infringement type"
                                items={buildTypeItems()}
                                selectedValue={query.type == null ? allOption : `${query.type}`}
                                onItemSelect={onTypeSelect}
                            />
                            <FormSelect
                                id="rules-engine-diff-label"
                                headingText="Label"
                                items={labelItems}
                                selectedValue={query.label ?? allOption}
                                onItemSelect={onLabelSelect}
                            />
                            <FormTextInput
                                id="rules-engine-diff-driver"
                                headingText="Driver"
                                placeholder="Employee id"
                                value={driverFilterText}
                                dangerHelperText={driverFilterError}
                                onChange={onDriverFilterChange}
                            />
                        </div>

                        <div className="spacer-bottom inline-items inline-items-wrap" id="rules-engine-diff-toggles">
                            <div className="inline-item">
                                <FormSwitch
                                    id="rules-engine-diff-show-provisional"
                                    label="Show provisional"
                                    inline
                                    alignment={Alignment.END}
                                    checked={query.provisional}
                                    onChange={onProvisionalToggle}
                                />
                            </div>
                            <div className="inline-item">
                                <FormSwitch
                                    id="rules-engine-diff-show-whitelisted"
                                    label="Show whitelisted"
                                    inline
                                    alignment={Alignment.END}
                                    checked={query.whitelisted}
                                    onChange={onWhitelistedToggle}
                                />
                            </div>
                            <div className="inline-item">
                                <FormSwitch
                                    id="rules-engine-diff-show-signed-off"
                                    label="Show signed off"
                                    inline
                                    alignment={Alignment.END}
                                    checked={query.signedOff}
                                    onChange={onSignedOffToggle}
                                />
                            </div>
                        </div>

                        <ShowHide
                            evaluator={showWhitelistedHidden}
                            show={(
                                <p id="rules-engine-diff-whitelisted-hidden" className="description-text">
                                    {`${whitelistedCount} whitelisted ${whitelistedCount === 1 ? "difference" : "differences"} hidden`}
                                </p>
                            )}
                        />

                        <DiffViewerGroups
                            groups={groups}
                            openSignature={query.group}
                            loading={groupsLoading}
                            failed={groupsFailed}
                            onToggleGroup={onToggleGroup}
                            onRetry={retryReads}
                        >
                            <DiffViewerDifferences
                                differences={groupDifferences}
                                page={query.page}
                                loading={groupLoading}
                                failed={groupFailed}
                                onRetry={loadGroupDifferences}
                                blockedReason={blockedReason}
                                onPageChange={onPageChange}
                                onOpenInHarness={onOpenInHarness}
                            />
                        </DiffViewerGroups>
                    </div>
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

RulesEngineDiffViewer.propTypes = {
    breadcrumbs: PropTypes.array
};
